<script setup lang="ts">
import { computed, type Component } from 'vue'
import {
  ICON_CHIP_ACCENT_CLASSES,
  ICON_CHIP_OUTLINE_CLASSES,
  ICON_CHIP_SELECTED_CLASSES,
  type IconChipAccent,
  type IconChipShape,
  type IconChipSize,
} from './icon-chip-types'

const props = withDefaults(
  defineProps<{
    icon: Component
    /** Accent recolouring the fill and glyph; `custom` reads `--icon-chip-accent` off the element instead of a token. Omit for the neutral chip. */
    accent?: IconChipAccent | 'custom'
    /** Chip size: `xs` (24px, inline marker) · `sm` (32px, list bullet) · `md` (40px, section medallion). */
    size?: IconChipSize
    /** Draws the accent as an inset ring over no fill instead of a tinted fill. */
    outlined?: boolean
    /** Fills the chip with the solid accent and an on-accent glyph, for the picked item. */
    selected?: boolean
    /** `tile` (squircle, default) or `disc` (circle). */
    shape?: IconChipShape
  }>(),
  { accent: undefined, size: 'md', outlined: false, selected: false, shape: 'tile' },
)

const CUSTOM_ACCENT_CLASSES = {
  filled: 'icon-chip-custom bg-(--icon-chip-accent-soft) text-(--icon-chip-accent)',
  outlined: 'text-(--icon-chip-accent) inset-ring-2 inset-ring-(--icon-chip-accent)',
}

const accentClass = computed(() => {
  if (props.accent === 'custom') {
    return props.outlined ? CUSTOM_ACCENT_CLASSES.outlined : CUSTOM_ACCENT_CLASSES.filled
  }
  const table = props.selected
    ? ICON_CHIP_SELECTED_CLASSES
    : props.outlined
      ? ICON_CHIP_OUTLINE_CLASSES
      : ICON_CHIP_ACCENT_CLASSES
  return table[props.accent ?? 'neutral']
})

const SIZE_CLASSES: Record<IconChipSize, string> = {
  xs: 'icon-sm size-12',
  sm: 'icon-sm size-16',
  md: 'icon-lg size-20',
}

const TILE_RADIUS: Record<IconChipSize, string> = {
  xs: 'rounded-2',
  sm: 'rounded-4',
  md: 'rounded-6',
}

const shapeClass = computed(() =>
  props.shape === 'disc' ? 'rounded-full' : TILE_RADIUS[props.size],
)
</script>

<template>
  <span
    :class="['grid shrink-0 place-items-center', shapeClass, SIZE_CLASSES[size], accentClass]"
    aria-hidden="true"
  >
    <component :is="icon" />
  </span>
</template>
